<script setup lang="ts">
/**
 * YfbIcon — 统一图标入口
 * - 集合：lucide（主，stroke-width=2.5 默认）/ ph Bold（辅，体育/操作类）/ simple-icons（品牌标，单色染品牌色）/ fhc（字体图标）
 * - 由 unplugin-icons 在构建时按需打包（每个图标 < 1KB）
 * - 使用：<YfbIcon name="lucide:settings" /> 或 <YfbIcon name="ph:soccer-ball-bold" />
 * - 品牌标：优先 brand:<key>（第三方登录/支付渠道，自动映射 simple-icons 并由调用方染品牌色）；
 *   也可直接用 simple-icons:<name>（如 simple-icons:alipay）
 * - fhc 字体图标：<YfbIcon name="fhc:migushipin" />（对应 fhc-icons-migushipin）
 * - 命名空间必带：lucide: / ph: / simple-icons: / fhc:
 */
import { computed, type FunctionalComponent } from 'vue'
import LucideCircle from '~icons/lucide/circle'
import LucideCircleDot from '~icons/lucide/circle-dot'
import LucideCheck from '~icons/lucide/check'
import LucideX from '~icons/lucide/x'
import LucideSparkles from '~icons/lucide/sparkles'
import LucideSettings from '~icons/lucide/settings'
import LucideMessageSquare from '~icons/lucide/message-square'
import LucideHistory from '~icons/lucide/history'
import LucideInfo from '~icons/lucide/info'
import LucideSun from '~icons/lucide/sun'
import LucideMoon from '~icons/lucide/moon'
import LucideMenu from '~icons/lucide/menu'
import LucidePlay from '~icons/lucide/play'
import LucideStar from '~icons/lucide/star'
import LucideDiamond from '~icons/lucide/diamond'
import LucideArrowLeft from '~icons/lucide/arrow-left'
import LucideArrowUpRight from '~icons/lucide/arrow-up-right'
import LucideClock from '~icons/lucide/clock'
import LucideMic from '~icons/lucide/mic'
import LucideLayers from '~icons/lucide/layers'
import LucideGlobe from '~icons/lucide/globe'
import LucideShieldCheck from '~icons/lucide/shield-check'
import LucideShieldOff from '~icons/lucide/shield-off'
import LucideLock from '~icons/lucide/lock'
import LucideRefreshCw from '~icons/lucide/refresh-cw'
import LucideZap from '~icons/lucide/zap'
import LucideTimer from '~icons/lucide/timer'
import LucideChevronRight from '~icons/lucide/chevron-right'
import LucideChevronDown from '~icons/lucide/chevron-down'
import LucideChevronUp from '~icons/lucide/chevron-up'
import LucideExternalLink from '~icons/lucide/external-link'
import LucideBriefcase from '~icons/lucide/briefcase'
import LucideTv from '~icons/lucide/tv'
import LucideMonitorPlay from '~icons/lucide/monitor-play'
import LucideMessageSquareWarning from '~icons/lucide/message-square-warning'
import LucideApple from '~icons/lucide/apple'
import LucideBot from '~icons/lucide/bot'
import LucideArrowRight from '~icons/lucide/arrow-right'
import LucideCrown from '~icons/lucide/crown'
import LucideDownload from '~icons/lucide/download'
import LucideBookOpen from '~icons/lucide/book-open'
import PhWechatLogoBold from '~icons/ph/wechat-logo-bold'
import PhCurrencyCnyBold from '~icons/ph/currency-cny-bold'
import PhCheckCircleBold from '~icons/ph/check-circle-bold'
import PhXCircleBold from '~icons/ph/x-circle-bold'
import PhSoccerBallBold from '~icons/ph/soccer-ball-bold'
import PhBasketballBold from '~icons/ph/basketball-bold'
import PhPlayCircleBold from '~icons/ph/play-circle-bold'
import PhLightningBold from '~icons/ph/lightning-bold'
import PhHourglassBold from '~icons/ph/hourglass-bold'
import PhEyeBold from '~icons/ph/eye-bold'
import PhEyeSlashBold from '~icons/ph/eye-slash-bold'
import PhMonitorBold from '~icons/ph/monitor-bold'
import PhFastForwardBold from '~icons/ph/fast-forward-bold'
import PhListBold from '~icons/ph/list-bold'
import PhPowerBold from '~icons/ph/power-bold'
import PhShieldBold from '~icons/ph/shield-bold'
import PhDeviceMobileBold from '~icons/ph/device-mobile-bold'
import PhDesktopBold from '~icons/ph/desktop-bold'
import PhStarBold from '~icons/ph/star-bold'
import PhTrophyBold from '~icons/ph/trophy-bold'
import PhMedalBold from '~icons/ph/medal-bold'
import PhGlobeBold from '~icons/ph/globe-bold'
import PhCaretDownBold from '~icons/ph/caret-down-bold'
import PhWarningBold from '~icons/ph/warning-bold'
import PhShieldCheckBold from '~icons/ph/shield-check-bold'
import SiAlipay from '~icons/simple-icons/alipay'
import SiWechat from '~icons/simple-icons/wechat'
import SiTencentqq from '~icons/simple-icons/tencentqq'
import SiGoogle from '~icons/simple-icons/google'
import SiApple from '~icons/simple-icons/apple'
import SiDouyin from '~icons/simple-icons/tiktok'
import SiGithub from '~icons/simple-icons/github'
import LogosGoogleIcon from '~icons/logos/google-icon'
import type { IconName } from '@/types/icons'

const REGISTRY: Record<IconName, FunctionalComponent> = {
  'lucide:circle': LucideCircle,
  'lucide:circle-dot': LucideCircleDot,
  'lucide:check': LucideCheck,
  'lucide:x': LucideX,
  'lucide:sparkles': LucideSparkles,
  'lucide:settings': LucideSettings,
  'lucide:message-square': LucideMessageSquare,
  'lucide:history': LucideHistory,
  'lucide:info': LucideInfo,
  'lucide:sun': LucideSun,
  'lucide:moon': LucideMoon,
  'lucide:menu': LucideMenu,
  'lucide:play': LucidePlay,
  'lucide:star': LucideStar,
  'lucide:diamond': LucideDiamond,
  'lucide:arrow-up-right': LucideArrowUpRight,
  'lucide:arrow-left': LucideArrowLeft,
  'lucide:clock': LucideClock,
  'lucide:mic': LucideMic,
  'lucide:layers': LucideLayers,
  'lucide:globe': LucideGlobe,
  'lucide:shield-check': LucideShieldCheck,
  'lucide:shield-off': LucideShieldOff,
  'lucide:lock': LucideLock,
  'lucide:refresh-cw': LucideRefreshCw,
  'lucide:zap': LucideZap,
  'lucide:timer': LucideTimer,
  'lucide:chevron-right': LucideChevronRight,
  'lucide:chevron-down': LucideChevronDown,
  'lucide:chevron-up': LucideChevronUp,
  'lucide:external-link': LucideExternalLink,
  'lucide:briefcase': LucideBriefcase,
  'lucide:tv': LucideTv,
  'lucide:monitor-play': LucideMonitorPlay,
  'lucide:message-square-warning': LucideMessageSquareWarning,
  'lucide:apple': LucideApple,
  'lucide:bot': LucideBot,
  'lucide:arrow-right': LucideArrowRight,
  'lucide:crown': LucideCrown,
  'lucide:download': LucideDownload,
  'lucide:book-open': LucideBookOpen,
  'ph:wechat-logo-bold': PhWechatLogoBold,
  'ph:currency-cny-bold': PhCurrencyCnyBold,
  'ph:check-circle-bold': PhCheckCircleBold,
  'ph:x-circle-bold': PhXCircleBold,
  'ph:soccer-ball-bold': PhSoccerBallBold,
  'ph:basketball-bold': PhBasketballBold,
  'ph:play-circle-bold': PhPlayCircleBold,
  'ph:lightning-bold': PhLightningBold,
  'ph:hourglass-bold': PhHourglassBold,
  'ph:eye-bold': PhEyeBold,
  'ph:eye-slash-bold': PhEyeSlashBold,
  'ph:monitor-bold': PhMonitorBold,
  'ph:fast-forward-bold': PhFastForwardBold,
  'ph:list-bold': PhListBold,
  'ph:power-bold': PhPowerBold,
  'ph:shield-bold': PhShieldBold,
  'ph:device-mobile-bold': PhDeviceMobileBold,
  'ph:desktop-bold': PhDesktopBold,
  'ph:star-bold': PhStarBold,
  'ph:trophy-bold': PhTrophyBold,
  'ph:medal-bold': PhMedalBold,
  'ph:globe-bold': PhGlobeBold,
  'ph:caret-down-bold': PhCaretDownBold,
  'ph:warning-bold': PhWarningBold,
  'ph:shield-check-bold': PhShieldCheckBold,
  'simple-icons:alipay': SiAlipay,
  'simple-icons:wechat': SiWechat,
  'simple-icons:tencentqq': SiTencentqq,
  'simple-icons:google': SiGoogle,
  'simple-icons:apple': SiApple,
  'simple-icons:tiktok': SiDouyin,
  'simple-icons:github': SiGithub,
  // logos: 官方全彩品牌标（自带 fill，不随 currentColor 染色）
  'logos:google-icon': LogosGoogleIcon
}

const props = withDefaults(
  defineProps<{
    name: string
    size?: number | string
    stroke?: number | string
  }>(),
  { size: 16, stroke: 2.5 }
)

const isFhcIcon = computed(() => props.name.startsWith('fhc:'))
const fhcClassName = computed(() => `fhc-icons fhc-icons-${props.name.slice(4)}`)

// brand: 命名空间 → 品牌标（第三方登录 logo / 支付渠道标）。
// 优先官方全彩版（logos: 集合，自带官方 fill）；无全彩版的品牌走 simple-icons
// 单色字形 + 官方规范色（由调用方染色的品牌色对齐官方 spec）；未知 key 兜底 lucide:globe
const BRAND_MAP: Record<string, IconName> = {
  wechat: 'simple-icons:wechat',
  qq: 'simple-icons:tencentqq',
  google: 'logos:google-icon',
  apple: 'simple-icons:apple',
  douyin: 'simple-icons:tiktok',
  github: 'simple-icons:github',
  alipay: 'simple-icons:alipay'
}

const isBrandIcon = computed(() => props.name.startsWith('brand:'))

const iconComp = computed<FunctionalComponent | undefined>(() => {
  if (isBrandIcon.value) {
    const mapped = BRAND_MAP[props.name.slice(6)]
    return mapped ? REGISTRY[mapped] : LucideGlobe
  }
  return REGISTRY[props.name as IconName]
})
</script>

<template>
  <!-- fhc 字体图标 -->
  <span
    v-if="isFhcIcon"
    class="fhc-icons"
    :class="fhcClassName"
    :style="{ fontSize: typeof size === 'number' ? `${size}px` : size }"
    aria-hidden="true"
  />
  <!-- lucide / ph / simple-icons（含 brand: 解析结果）SVG 图标 -->
  <component
    v-else
    :is="iconComp"
    :width="size"
    :height="size"
    :stroke-width="stroke"
    aria-hidden="true"
  />
</template>
